import React, { useEffect, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { Col, Row } from "react-bootstrap";
import { DateRangeCalendar, ReactSelect, SpinnerLoader, TextareaInputWithCounter } from "@services/ui-components";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { convertDateFormat } from "@me-team/host/src/utils/utils";
import {
    useGetAllEmployeesQuery,
    useGetOneSalaryPaymentQuery,
    useUpdatePaymentMutation,
} from "@me-team/host/main/salary/graphql/salary.hooks";
import { SalaryPaymentInput } from "@me-team/host/main/graphql/types";
import { currencySingVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import { addPaymentFormValidationSchema } from "../Salary/constans";

interface EditPaymentFormProps {
    handleModalClose: () => void;
    paymentId: number;
}

interface EditPaymentFormValues {
    date: string;
    dateRangeFilter: {
        startDate: string;
        endDate: string;
    };
    employeeId: number;
    quantity: number;
    sum: number;
    comment?: string;
}

const EditPaymentForm: React.FC<EditPaymentFormProps> = ({ handleModalClose, paymentId }) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currencySing = useReactiveVar(currencySingVar);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [updateSalaryPayment] = useUpdatePaymentMutation();

    const { data: oneSalaryPaymentData, loading } = useGetOneSalaryPaymentQuery({
        variables: {
            id: paymentId && paymentId,
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId || !paymentId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const payment = oneSalaryPaymentData && oneSalaryPaymentData?.user?.company?.salaryPayments?.salaryPayments[0];

    const { data: employeesData } = useGetAllEmployeesQuery({
        variables: { companyId: +currentCompanyId, branchId: +branchId },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const periodStart = payment?.periodStart ?? null;
    const periodEnd = payment?.periodEnd ?? null;

    useEffect(() => {
        if (!periodStart || !periodEnd) return;

        setDateRangeFilter((prev) => {
            const isSame = prev?.startDate === periodStart && prev?.endDate === periodEnd;

            return isSame ? prev : { startDate: periodStart, endDate: periodEnd };
        });
    }, [periodStart, periodEnd]);

    const initialFiltersRange = {
        startDate: payment?.periodStart && payment?.periodStart,
        endDate: payment?.periodEnd && payment?.periodEnd,
    };

    const [dateRangeFilter, setDateRangeFilter] = useState(initialFiltersRange);

    const employeesOptions =
        employeesData?.user?.company?.employees?.employees?.map((employee) => {
            return {
                value: employee?.id,
                label: `${employee?.name} ${employee?.surname}`,
            };
        }) || [];

    const initialValues: EditPaymentFormValues = {
        date: payment?.createdAt && convertDateFormat(payment?.createdAt, "dd.LL.yyyy"),
        dateRangeFilter: {
            startDate: payment?.periodStart ?? null,
            endDate: payment?.periodEnd ?? null,
        },
        employeeId: payment?.employee?.id ?? null,
        quantity: oneSalaryPaymentData?.user?.company?.salaryPayments?.salaryPayments?.[0]?.bookings?.bookings?.length,
        sum: payment?.paymentValue,
        comment: payment?.comment ? payment?.comment : null,
    };

    const handleEditPaymentSubmit = (
        values: EditPaymentFormValues,
        { setSubmitting }: FormikHelpers<EditPaymentFormValues>
    ) => {
        const input: SalaryPaymentInput = {
            periodStart: values.dateRangeFilter.startDate,
            periodEnd: values.dateRangeFilter.endDate,
            comment: values?.comment ? values?.comment : "",
        };
        updateSalaryPayment({
            variables: {
                employeeId: values?.employeeId,
                salaryPaymentId: payment?.id && payment?.id,
                input,
                companyId: +currentCompanyId,
                branchId: +branchId,
            },
            context: {
                errorType: "local",
            },
            onCompleted: (data) => {
                data && handleModalClose();
                setSubmitting(false);
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
                setSubmitting(false);
            },
        });
    };

    if (loading) return <Spinner />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={handleEditPaymentSubmit}
                validationSchema={addPaymentFormValidationSchema(t)}
            >
                {({ isSubmitting, setFieldValue, values, isValid, handleReset }) => {
                    return (
                        <Form>
                            <Row className="align-items-center mt-2 mt-sm-3 pe-3 ps-6 py-2">
                                <Col sm={3} className="text-start mb-2 mb-sm-0">
                                    <span className="fs-7 fw-bold required-label">{t("Date")}</span>
                                </Col>
                                <Col sm={9}>
                                    <p className="text-start m-0 fs-7"> {values.date}</p>
                                </Col>
                            </Row>
                            <Row className="align-items-center mt-2 mt-sm-3 pe-3 py-2 ps-6">
                                <Col sm={3} className="text-start mb-2 mb-sm-0">
                                    <label htmlFor="dateRangeFilter">
                                        <span className="fs-7 fw-bold required-label">{t("Period Payments")}</span>
                                    </label>
                                </Col>
                                <Col sm={9}>
                                    <DateRangeCalendar
                                        name="dateRangeFilter"
                                        dateRangeFilter={dateRangeFilter}
                                        setDateRangeFilter={(nextRange) => {
                                            setDateRangeFilter(nextRange);
                                            setFieldValue("dateRangeFilter.startDate", nextRange?.startDate ?? null);
                                            setFieldValue("dateRangeFilter.endDate", nextRange?.endDate ?? null);
                                        }}
                                        id="editPayment-dateRange-field"
                                        hideSubLabel
                                    />
                                </Col>
                            </Row>
                            <Row className="mt-2 mt-sm-3 pe-3 py-2 align-items-center ps-6">
                                <Col sm={3} className="text-start mb-2 mb-sm-0">
                                    <label htmlFor="employeeId">
                                        <span className="fs-7 fw-bold text-start required-label">{t("Employee")}</span>
                                    </label>
                                </Col>
                                <Col sm={9} className="text-start">
                                    <ReactSelect
                                        name="employeeId"
                                        id="editPayment-employeeId-field"
                                        value={employeesOptions.find((opt) => opt.value === values.employeeId) ?? null}
                                        options={employeesOptions}
                                        placeholder={t("employee")}
                                        onChange={(selectedOption) =>
                                            setFieldValue("employeeId", selectedOption?.value)
                                        }
                                        isSearchable={true}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center mt-2 mt-sm-3 pe-3 py-2 ps-6">
                                <Col sm={3} className="text-start mb-2 mb-sm-0">
                                    <span className="fs-7 fw-bold">{t("Services performed")}</span>
                                </Col>
                                <Col sm={9}>
                                    <p className="text-start m-0">{values.quantity}</p>
                                </Col>
                            </Row>
                            <Row className="align-items-center mt-2 mt-sm-3 pe-3 py-2 ps-6">
                                <Col sm={3} className="text-start mb-2 mb-sm-0">
                                    <span className="fs-7 fw-bold">{t("Payment amount")}</span>
                                </Col>
                                <Col sm={9}>
                                    <p className="text-start m-0">
                                        {values.sum}
                                        <span className="ms-1">{currencySing}</span>
                                    </p>
                                </Col>
                            </Row>
                            <Row className="mt-2 mt-sm-3 pe-3 py-2 ps-6">
                                <TextareaInputWithCounter
                                    id="editPayment-comment-field"
                                    name="comment"
                                    maxLength={200}
                                    labelCol={3}
                                    inputCol={9}
                                    placeholder={t("Add Comment")}
                                    label={t("Comment")}
                                />
                            </Row>
                            <div className="d-flex m-0 mt-2 mt-sm-3 justify-content-end gx-2">
                                <Button
                                    onClick={() => {
                                        handleReset();
                                        handleModalClose();
                                    }}
                                    variant="outline-primary"
                                    type="button"
                                    disabled={isSubmitting}
                                    className="custom-btn fw-normal me-2"
                                >
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="custom-btn"
                                    disabled={isSubmitting || !isValid}
                                >
                                    {isSubmitting ? (
                                        <SpinnerLoader variant="light" loadingText={t("Editing...")} />
                                    ) : (
                                        t("edit")
                                    )}
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default EditPaymentForm;
